import { useEffect, useState, useMemo } from 'react';
import { apiClient } from '@/lib/apiClient';
import { CheckCircle, XCircle, Clock, FileText, Send, Eye, Package, X, Search, MessageCircle, Filter, Store, Building } from 'lucide-react';

const toPersianDigits = (str) => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.toString().replace(/\d/g, (d) => persianDigits[d]).replace(/\./g, '٫');
};

const fmt = (n) => {
    const num = Number(n) || 0;
    return toPersianDigits(new Intl.NumberFormat('en-US').format(num));
};

const fmtPercent = (n) => {
    const num = Number(n);
    return toPersianDigits(num % 1 === 0 ? `${num}` : `${num.toFixed(1)}`) + '٪';
};

export default function SuperAdminContractsPage() {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [activeChatId, setActiveChatId] = useState(null);
    const [chatMessage, setChatMessage] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [itemsModalContract, setItemsModalContract] = useState(null);

    const loadContracts = async () => {
        setLoading(true);
        try {
            const { data, error } = await apiClient.GET('/api/v1/super_admin/contracts');
            if (error) throw new Error(error.detail || 'خطا در دریافت قراردادها');
            setContracts(data || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadContracts();
    }, []);

    const handleApprove = async (id) => {
        if (!window.confirm('آیا از تایید نهایی و فعال‌سازی این قرارداد اطمینان دارید؟')) return;
        try {
            const { error } = await apiClient.POST(`/api/v1/super_admin/contracts/${id}/approve`);
            if (error) throw new Error(error.detail || 'خطا در تایید قرارداد');
            alert('قرارداد با موفقیت تایید نهایی و فعال شد.');
            loadContracts();
        } catch (e) {
            alert(e.message);
        }
    };

    const handleReject = async (id) => {
        const reason = prompt('لطفاً دلیل رد/لغو قرارداد را وارد کنید:');
        if (!reason) return;
        try {
            const { error } = await apiClient.POST(`/api/v1/super_admin/contracts/${id}/reject`, {
                body: { rejection_reason: reason }
            });
            if (error) throw new Error(error.detail || 'خطا در رد قرارداد');
            alert('قرارداد لغو شد.');
            loadContracts();
        } catch (e) {
            alert(e.message);
        }
    };

    const handleSendMessage = async (contractId) => {
        if (!chatMessage.trim()) return;
        try {
            await apiClient.POST(`/api/v1/super_admin/contracts/${contractId}/messages`, {
                body: { message: chatMessage }
            });
            setChatMessage('');
            loadContracts();
        } catch (e) {
            alert(e.message);
        }
    };

    const calculateTotals = (items, contractDiscount) => {
        let baseTotal = 0;
        let discountedTotal = 0;
        items?.forEach(item => {
            const price = Number(item.base_price) || 0;
            const qty = Number(item.max_qty) || 1;
            const lineBase = price * qty;
            baseTotal += lineBase;

            const discount = Number(item.discount_percent_override || contractDiscount || 0);
            discountedTotal += lineBase * (1 - discount / 100);
        });
        return { baseTotal, discountedTotal };
    };

    const filteredContracts = useMemo(() => {
        return contracts.filter(c => {
            const matchStatus = statusFilter === 'all' || (c.status || '').toLowerCase() === statusFilter;
            const matchSearch = (c.company_code || '').toLowerCase().includes(searchQuery.toLowerCase());
            return matchStatus && matchSearch;
        });
    }, [contracts, statusFilter, searchQuery]);

    if (loading && contracts.length === 0) return <div style={{ textAlign: 'center', padding: '3rem' }}>در حال دریافت اطلاعات...</div>;

    return (
        <div style={{ direction: 'rtl', width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <h1 style={{ fontSize: '1.4rem', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FileText size={22} color="#2563eb" /> مدیریت قراردادهای سازمانی (B2B)
                </h1>
            </header>

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '0.75rem', borderRadius: '8px', fontSize: '0.9rem' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '0.75rem', background: '#fff', padding: '0.75rem', borderRadius: '10px', border: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, position: 'relative', minWidth: '200px' }}>
                    <Search size={16} color="#94a3b8" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                        type="text"
                        placeholder="جستجوی کد قرارداد..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem 2rem 0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '0.85rem' }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f8fafc', padding: '0.2rem 0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                    <Filter size={16} color="#64748b" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ border: 'none', background: 'transparent', padding: '0.3rem', outline: 'none', fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                        <option value="all">همه وضعیت‌ها</option>
                        <option value="pending_approval">در انتظار تایید</option>
                        <option value="active">فعال</option>
                        <option value="rejected">لغو شده</option>
                    </select>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {filteredContracts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1', color: '#64748b' }}>
                        هیچ قراردادی با این مشخصات یافت نشد.
                    </div>
                ) : filteredContracts.map(c => {
                    const statusLower = (c.status || '').toLowerCase();
                    const isPending = statusLower === 'pending_approval';
                    const isActive = statusLower === 'active';
                    const isRejected = statusLower === 'rejected';
                    const isChatOpen = activeChatId === c.id;
                    const vendorApproved = !!c.vendor_approved_at;

                    return (
                        <div key={c.id} style={{
                            /* === [CHANGED] فعال بودن با کادر سبز پررنگ مشخص می‌شود === */
                            background: isActive ? '#d1fae5' : '#fff',
                            borderRadius: '10px',
                            border: isActive ? '2px solid #10b981' : '1px solid #e5e7eb',
                            padding: '0.75rem 1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                            boxShadow: isActive ? '0 4px 6px -1px rgba(16, 185, 129, 0.15)' : '0 1px 2px rgba(0,0,0,0.02)',
                            transition: 'all 0.3s ease'
                        }}>

                            {/* ردیف بالا: کد، نام شرکت/فروشنده، وضعیت و دکمه‌ها */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>

                                {/* سمت راست: کد و نام‌ها */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <h3 style={{ margin: 0, fontSize: '1rem', color: isActive ? '#064e3b' : '#1e293b' }}>
                                            کد: <span style={{ color: isActive ? '#047857' : '#2563eb', fontFamily: 'monospace' }}>{c.company_code}</span>
                                        </h3>
                                        <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem', color: isActive ? '#065f46' : '#475569', background: isActive ? '#ecfdf5' : '#f8fafc', padding: '0.2rem 0.5rem', borderRadius: '6px', border: isActive ? '1px solid #a7f3d0' : '1px solid #e2e8f0' }}>
                                            <span>تخفیف: <strong style={{ color: '#ef4444' }}>{c.discount_percent ? fmtPercent(c.discount_percent) : '-'}</strong></span>
                                            <span>اقلام: <strong>{toPersianDigits(c.basket_items?.length || 0)}</strong></span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.75rem', color: isActive ? '#065f46' : '#475569' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                            <Building size={12} color={isActive ? '#047857' : '#64748b'} />
                                            شرکت: <strong>{c.company_name || 'نامشخص'}</strong>
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                            <Store size={12} color={isActive ? '#047857' : '#64748b'} />
                                            فروشنده: <strong>{c.vendor_name || 'نامشخص'}</strong>
                                        </span>
                                    </div>
                                </div>

                                {/* وسط: وضعیت (لیبل فعال حذف شد) */}
                                <div>
                                    {isRejected && <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>لغو شده</span>}
                                    {isPending && vendorApproved && <span style={{ background: '#fef3c7', color: '#92400e', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>در انتظار تایید نهایی</span>}
                                    {isPending && !vendorApproved && <span style={{ background: '#f1f5f9', color: '#64748b', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.3rem' }}><Clock size={12}/> در انتظار فروشنده</span>}
                                </div>

                                {/* سمت چپ: دکمه‌های عملیات */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <button onClick={() => setItemsModalContract(c)} style={{ background: isActive ? '#fff' : '#eff6ff', color: isActive ? '#047857' : '#2563eb', border: isActive ? '1px solid #a7f3d0' : '1px solid #bfdbfe', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                        <Eye size={14} /> مشاهده اقلام
                                    </button>
                                    <button onClick={() => setActiveChatId(isChatOpen ? null : c.id)} style={{ background: isActive ? '#fff' : '#f8fafc', color: '#475569', border: isActive ? '1px solid #a7f3d0' : '1px solid #e2e8f0', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem' }}>
                                        <MessageCircle size={14} /> پیام‌ها
                                    </button>

                                    {isPending && vendorApproved && (
                                        <>
                                            <button onClick={() => handleReject(c.id)} style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                                لغو
                                            </button>
                                            <button onClick={() => handleApprove(c.id)} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                                تایید نهایی
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* دلیل لغو (در صورت وجود) */}
                            {isRejected && c.rejection_reason && (
                                <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', border: '1px solid #fecaca', marginTop: '0.5rem' }}>
                                    <strong>دلیل لغو:</strong> {c.rejection_reason}
                                </div>
                            )}

                            {/* چت */}
                            {isChatOpen && (
                                <div style={{ marginTop: '0.25rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem' }}>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', marginBottom: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {(!c.messages || c.messages.length === 0) ? (
                                            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>پیامی وجود ندارد.</div>
                                        ) : c.messages.map(m => (
                                            <div key={m.id} style={{ alignSelf: m.sender_role === 'super_admin' ? 'flex-start' : 'flex-end', maxWidth: '80%', background: m.sender_role === 'super_admin' ? '#e0f2fe' : '#fff', border: m.sender_role === 'super_admin' ? 'none' : '1px solid #e2e8f0', padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.8rem' }}>
                                                <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: '0.2rem' }}>
                                                    {m.sender_role === 'super_admin' ? 'مدیر سامانه' : m.sender_role === 'vendor' ? 'تامین‌کننده' : 'شرکت'}
                                                </div>
                                                {m.message}
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                                        <input
                                            type="text"
                                            value={chatMessage}
                                            onChange={e => setChatMessage(e.target.value)}
                                            placeholder="پیام..."
                                            style={{ flex: 1, padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem', outline: 'none' }}
                                            onKeyDown={e => e.key === 'Enter' && handleSendMessage(c.id)}
                                        />
                                        <button onClick={() => handleSendMessage(c.id)} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '0 0.75rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <Send size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* مودال مشاهده اقلام */}
            {itemsModalContract && (
                <div onClick={() => setItemsModalContract(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '92%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '12px', padding: '1.5rem', position: 'relative', direction: 'rtl' }}>
                        <button onClick={() => setItemsModalContract(null)} style={{ position: 'absolute', top: '1rem', left: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#ef4444" /></button>

                        <h3 style={{ marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.2rem', color: '#1e293b' }}>
                            <Package size={22} color="#2563eb" />
                            اقلام قرارداد <span style={{ fontFamily: 'monospace', color: '#2563eb' }}>{itemsModalContract.company_code}</span>
                        </h3>

                        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.9rem', color: '#475569', background: '#f8fafc', padding: '1rem', borderRadius: '8px', textAlign: 'center', border: '1px solid #e2e8f0' }}>
                            <div>شرکت: <strong>{itemsModalContract.company_name}</strong></div>
                            <div>فروشنده: <strong>{itemsModalContract.vendor_name}</strong></div>
                            <div>نوع قرارداد: <strong>{itemsModalContract.contract_type === 'discount' ? 'تخفیف' : 'کیف پول'}</strong></div>
                            <div>درصد تخفیف: <strong style={{ color: '#ef4444' }}>{itemsModalContract.discount_percent ? fmtPercent(itemsModalContract.discount_percent) : '—'}</strong></div>
                        </div>

                        <div className="admin-table-wrap" style={{ border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <tr>
                                    <th style={{ padding: '0.75rem', textAlign: 'center', width: '50px', color: '#475569' }}>#</th>
                                    <th style={{ padding: '0.75rem', textAlign: 'right', color: '#475569' }}>نام کالا</th>
                                    <th style={{ padding: '0.75rem', textAlign: 'center', color: '#475569' }}>تعداد</th>
                                    <th style={{ padding: '0.75rem', textAlign: 'center', color: '#475569' }}>قیمت پایه</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(itemsModalContract.basket_items || []).map((item, idx) => (
                                    <tr key={item.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '0.75rem', color: '#64748b', textAlign: 'center' }}>{toPersianDigits(idx + 1)}</td>
                                        <td style={{ padding: '0.75rem', fontWeight: 'bold', color: '#1e293b' }}>{item.product_name || 'کالای نامشخص'}</td>
                                        <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 'bold' }}>{toPersianDigits(item.max_qty || 1)}</td>
                                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{item.base_price != null ? fmt(item.base_price) : '—'}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* خلاصه مبالغ در پایین مودال */}
                        {itemsModalContract.basket_items && itemsModalContract.basket_items.length > 0 && (() => {
                            const { baseTotal, discountedTotal } = calculateTotals(itemsModalContract.basket_items, itemsModalContract.discount_percent);
                            return (
                                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '2rem', background: '#f8fafc', padding: '1rem 1.5rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <span style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '0.3rem' }}>مبلغ کل (بدون تخفیف):</span>
                                        <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#94a3b8', textDecoration: 'line-through' }}>{fmt(baseTotal)} ریال</span>
                                    </div>
                                    <div style={{ width: '1px', height: '40px', background: '#cbd5e1' }}></div>
                                    <div style={{ textAlign: 'center' }}>
                                        <span style={{ display: 'block', fontSize: '0.85rem', color: '#15803d', marginBottom: '0.3rem', fontWeight: 'bold' }}>مبلغ نهایی با تخفیف:</span>
                                        <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#15803d' }}>{fmt(discountedTotal)} ریال</span>
                                    </div>
                                </div>
                            );
                        })()}

                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button onClick={() => setItemsModalContract(null)} className="btn-primary" style={{ padding: '0.6rem 2rem', borderRadius: '8px' }}>بستن</button>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );
}